/**
 * Alteração em lote do pinout interno — observação, fonte e tipo.
 *
 * Três, e não as oito do pinout externo, porque do lado de dentro não existe
 * cor, chicote nem restrição para alterar.
 *
 * **O desfazer usa o mesmo truque do lado de fora**, e vale repetir por quê: o
 * endpoint grava um valor só para todos os ids e não devolve os anteriores. Só
 * que a grade tem os anteriores em mãos — são as linhas na tela. Então o
 * desfazer agrupa as linhas pelo valor que elas tinham e manda **uma chamada
 * por valor distinto**: se 12 linhas tinham três observações diferentes, são
 * três chamadas para devolver cada grupo à sua.
 */

import { useMemo, useState } from "react"
import {
  usarExcluirInternos, usarLoteInterno,
  type CampoDeLoteInterno, type PinoutInternoResponse,
} from "@/api/internos"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { TIPOS_PINOUT } from "../tipos"

type Operacao = {
  campo: CampoDeLoteInterno
  rotulo: string
  /** O valor que a linha tinha — é o que torna o desfazer possível. */
  anterior: (p: PinoutInternoResponse) => string | number
  tipoDeCampo: "texto" | "tipo"
}

const OPERACOES: Operacao[] = [
  { campo: "obs", rotulo: "Observação", anterior: (p) => p.obs ?? "", tipoDeCampo: "texto" },
  { campo: "arquivo-original", rotulo: "Fonte", anterior: (p) => p.arquivoOriginal ?? "", tipoDeCampo: "texto" },
  { campo: "tipo", rotulo: "Tipo", anterior: (p) => p.tipo ?? 0, tipoDeCampo: "tipo" },
]

type Feito = { operacao: Operacao; grupos: Map<string | number, number[]>; quantos: number }

export function LoteInterno({
  projetoId, componenteId, marcados, linhas, aoLimpar,
}: {
  projetoId: number
  componenteId: number
  marcados: Set<number>
  /** As linhas da página, para ler o valor anterior de cada marcada. */
  linhas: PinoutInternoResponse[]
  aoLimpar: () => void
}) {
  const lote = usarLoteInterno(projetoId, componenteId)
  const excluir = usarExcluirInternos(projetoId, componenteId)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false)
  const [aberta, setAberta] = useState<Operacao | null>(null)
  const [valor, setValor] = useState<string>("")
  const [feito, setFeito] = useState<Feito | null>(null)

  const ids = useMemo(() => [...marcados], [marcados])
  const naPagina = useMemo(() => linhas.filter((l) => marcados.has(l.id)), [linhas, marcados])
  const foraDaPagina = ids.length - naPagina.length

  function aplicar(op: Operacao) {
    // Guarda o antes ANTES de gravar: depois da chamada a grade já mudou.
    const grupos = new Map<string | number, number[]>()
    for (const l of naPagina) {
      const antes = op.anterior(l)
      const lista = grupos.get(antes) ?? []
      lista.push(l.id)
      grupos.set(antes, lista)
    }

    lote.mutate(
      { campo: op.campo, ids, valor: op.tipoDeCampo === "tipo" ? Number(valor) : valor },
      {
        onSuccess: () => {
          setFeito({ operacao: op, grupos, quantos: ids.length })
          setAberta(null)
          setValor("")
        },
      },
    )
  }

  function desfazer(f: Feito) {
    for (const [antes, deQuem] of f.grupos) {
      lote.mutate({ campo: f.operacao.campo, ids: deQuem, valor: antes })
    }
    setFeito(null)
  }

  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 0,
      background: "var(--surface)", borderTop: "1px solid var(--brand)",
    }}>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, padding: "9px 18px" }}>
        <b style={{ fontSize: 12, color: "var(--brand-on-soft)" }}>
          {ids.length} {ids.length === 1 ? "linha marcada" : "linhas marcadas"}
        </b>
        <button onClick={aoLimpar} style={{ fontSize: 11.5, color: "var(--brand-on-soft)", opacity: 0.8 }}>
          desmarcar
        </button>

        <span style={{ width: 1, height: 18, background: "var(--line2)", margin: "0 2px" }} />

        {OPERACOES.map((op) => (
          <button
            key={op.campo}
            onClick={() => { setAberta(aberta?.campo === op.campo ? null : op); setValor("") }}
            aria-pressed={aberta?.campo === op.campo}
            style={{
              height: 26, padding: "0 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
              border: "1px solid var(--line2)",
              background: aberta?.campo === op.campo ? "var(--brand-soft)" : "var(--surface)",
              color: aberta?.campo === op.campo ? "var(--brand-on-soft)" : "var(--txt2)",
            }}
          >
            {op.rotulo}
          </button>
        ))}

        {/* Exclusão é a exceção honesta: linha apagada não volta e o endpoint
            não devolve o que apagou. Ali a confirmação é a única rede, e por
            isso ela é mais dura — dois cliques, com a contagem à vista. */}
        {confirmandoExclusao ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: 11.5, color: "var(--erro)" }}>
              Excluir {ids.length} {ids.length === 1 ? "linha" : "linhas"}? Não há como desfazer.
            </span>
            <button
              onClick={() => excluir.mutate(ids, { onSuccess: () => { setConfirmandoExclusao(false); aoLimpar() } })}
              disabled={excluir.isPending}
              style={{
                height: 26, padding: "0 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                background: "var(--erro)", color: "#FFFFFF",
              }}
            >
              {excluir.isPending ? "Excluindo…" : "Confirmar"}
            </button>
            <button onClick={() => setConfirmandoExclusao(false)}
                    style={{ fontSize: 11.5, color: "var(--txt2)" }}>
              cancelar
            </button>
          </span>
        ) : (
          <button
            onClick={() => setConfirmandoExclusao(true)}
            style={{
              height: 26, padding: "0 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
              color: "var(--erro)", border: "1px solid var(--erro)", background: "var(--surface)",
            }}
          >
            Excluir
          </button>
        )}

        {feito && (
          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>
              {feito.quantos} {feito.quantos === 1 ? "alterada" : "alteradas"}
            </span>
            {foraDaPagina === 0 ? (
              <button
                onClick={() => desfazer(feito)}
                style={{ fontSize: 11.5, fontWeight: 600, color: "var(--brand-on-soft)" }}
              >
                desfazer{feito.grupos.size > 1 ? ` (${feito.grupos.size} valores)` : ""}
              </button>
            ) : (
              // Sem os valores anteriores de todas as linhas, um desfazer
              // parcial deixaria metade certa e metade errada — pior que não
              // ter desfazer.
              <span title="Há linhas marcadas fora desta página; os valores anteriores delas não estão na tela."
                    style={{ fontSize: 11.5, color: "var(--txt3)" }}>
                sem desfazer
              </span>
            )}
          </span>
        )}
      </div>

      {aberta && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10, padding: "9px 18px",
          borderTop: "1px solid var(--line)", background: "var(--surface2)",
        }}>
          <span style={{ fontSize: 12, color: "var(--txt2)" }}>
            {aberta.rotulo} de <b style={{ color: "var(--txt)" }}>{ids.length}</b>{" "}
            {ids.length === 1 ? "linha" : "linhas"} para
          </span>

          {aberta.tipoDeCampo === "tipo" ? (
            <select
              value={valor} onChange={(e) => setValor(e.target.value)} aria-label={aberta.rotulo}
              style={{ height: 28, padding: "0 9px", borderRadius: 7, border: "1px solid var(--line2)",
                       background: "var(--surface)", fontSize: 12.5, minWidth: 170 }}
            >
              <option value="">escolha…</option>
              {Object.entries(TIPOS_PINOUT).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          ) : (
            <input
              value={valor} onChange={(e) => setValor(e.target.value)} aria-label={aberta.rotulo}
              placeholder="(vazio apaga o que está lá)"
              style={{ height: 28, padding: "0 9px", borderRadius: 7, border: "1px solid var(--line2)",
                       background: "var(--surface)", fontSize: 12.5, minWidth: 240 }}
            />
          )}

          <button
            onClick={() => aplicar(aberta)}
            disabled={lote.isPending || (aberta.tipoDeCampo === "tipo" && valor === "")}
            style={{
              height: 28, padding: "0 12px", borderRadius: 7, fontSize: 12, fontWeight: 600,
              background: "var(--brand)", color: "#FFFFFF", opacity: lote.isPending ? 0.6 : 1,
            }}
          >
            {lote.isPending ? "Aplicando…" : "Aplicar"}
          </button>
          <button onClick={() => setAberta(null)}
                  style={{ height: 28, padding: "0 9px", fontSize: 12, color: "var(--txt2)" }}>
            Cancelar
          </button>

          {foraDaPagina > 0 && (
            <span style={{ fontSize: 11, color: "var(--acao-on-soft)", maxWidth: "40ch", lineHeight: 1.45 }}>
              {foraDaPagina} {foraDaPagina === 1 ? "marcada está" : "marcadas estão"} fora desta
              página — a alteração vale para todas, mas o desfazer não.
            </span>
          )}
        </div>
      )}

      {lote.error != null && (
        <div style={{
          padding: "9px 18px", fontSize: 11.5, lineHeight: 1.5,
          background: lote.error instanceof ErroDeTrava ? "var(--acao-soft)" : "var(--erro-soft)",
          color: lote.error instanceof ErroDeTrava ? "var(--acao-on-soft)" : "var(--erro-on-soft)",
        }}>
          {lote.error instanceof ErroDaApi || lote.error instanceof Error
            ? lote.error.message : "Não foi possível alterar."}
        </div>
      )}
    </div>
  )
}
